<template>
  <div class="shadow-top py-1.5 xl:py-3 px-1.5 lg:px-4 bg-grey-975">
    <div
      class="flex flex-col items-start gap-1 bg-white px-1 lg:px-2 py-2 border border-blue-france-sun-113 rounded-md"
    >
      <div v-if="!newElement" class="font-bold">
        {{ authStore.currentUser?.firstName }}
        {{ authStore.currentUser?.lastName }}
      </div>
      <div v-if="newElement" class="p-2 bg-white w-full rounded-lg">
        <SelectUser data-sentry-id="AddFluxUserInput" v-model="agentsDirectoryEntry" ref="selectUserEl" with-c-c />
        <div class="flex flex-row-reverse justify-start gap-1">
          <PButton
            data-sentry-id="AddFluxUserBtn"
            mode="secondary"
            :disabled="agentsDirectoryEntry.length === 0"
            @click="addElement"
          >
            Ajouter
          </PButton>
          <PButton data-sentry-id="CancelAddFluxUserBtn" mode="secondary" color="error" @click="closeNewElement">
            Annuler
          </PButton>
        </div>
      </div>
      <div
        v-else-if="isCurrent || (isDraft && isCurrent)"
        class="flex flex-col items-stretch lg:items-baseline gap-1 w-full"
      >
        <PButton
          v-if="isDeletable"
          data-sentry-id="CancelLetterFileCreationBtn"
          mode="secondary"
          color="error"
          @click="isDeleteLFModalOpen = true"
        >
          Je supprime le parapheur
        </PButton>
        <div class="flex items-stretch flex-col lg:flex-row gap-1 w-full">
          <div v-if="isCurrent" class="flex flex-col items-stretch lg:items-center order-2">
            <PButton data-sentry-id="AddNewStepBtn" v-show="!newElement" mode="secondary" @click="openNewElement">
              Ajouter une autre étape
            </PButton>
          </div>
          <PTooltip
            :label="nextElementLabel"
            placement="top"
            :offset="5"
            activator-wrapper-classes="h-full"
            class="[&_.bg-grey-425]:max-w-[400px] order-3"
          >
            <PButton
              data-sentry-id="GoNextStepBtn"
              class="h-full w-full"
              :loading="isNextStepLoading"
              @click="nextStep"
            >
              Passer ce parapheur à l'étape suivante
            </PButton>
          </PTooltip>
          <div class="flex justify-end grow order-1 lg:order-12">
            <CloneButton class="w-full lg:w-auto" />
          </div>
        </div>
      </div>
      <div v-else-if="!isDraft" class="flex items-stretch justify-between flex-col lg:flex-row gap-1 w-full">
        <PButton
          data-sentry-id="UnarchiveLetterFileBtn"
          mode="secondary"
          @click="unarchive"
          v-if="letterFile?.archived"
          class="order-3"
        >
          Je désarchive le parapheur
        </PButton>
        <PButton data-sentry-id="TakeLetterFileBtn" mode="secondary" @click="take" v-else class="order-4">
          Je prends la main
        </PButton>
        <PButton
          v-if="isDeletable && letterFile?.archived"
          data-sentry-id="CancelLetterFileCreationBtn"
          mode="secondary"
          color="error"
          class="order-2 lg:order-5"
          @click="isDeleteLFModalOpen = true"
        >
          Je supprime le parapheur
        </PButton>
        <CloneButton class="w-full lg:w-auto order-1 lg:order-12" />
      </div>
    </div>

    <DeleteLetterFileModal
      :open="isDeleteLFModalOpen"
      @cancel="isDeleteLFModalOpen = false"
      @confirm="deleteLetterFile"
    />
  </div>
</template>

<script setup lang="ts">
import DeleteLetterFileModal from "@/app/components/DeleteLetterFileModal.vue";
import { AlertDuration, AlertType, PButton, PTooltip, useAlertManager, usePastaRouter } from "@pasta/box-v2";
import { useAuthStore } from "@pasta/front-auth-v2";
import { computed, inject, nextTick, ref } from "vue";
import SelectUser from "../../../components/SelectUser.vue";
import { useNavigationChecker } from "../../../composable/navigation-checker";
import { useNotifyAsyncOperation } from "../../../composable/useAsyncOperation";
import { useShowSignedNotified } from "../../../composable/useShowSignedNotified";
import { type AgentsDirectoryAutocompleteItem } from "../../../dto/agents-directory.dto";
import { LetterFileStatus } from "../../../dto/letter-file.dto";
import { useLetterFileStore } from "../../../stores/letter-file.store";
import { FormOperation } from "@/app/types";
import { canGoNext, type FormProvide, FormToken } from "../utils";
import CloneButton from "./CloneButton.vue";

interface Props {
  next: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "undraft"): void;
  (e: "newDownstream"): void;
}>();

const letterFileStore = useLetterFileStore();
const notifyAsyncOperation = useNotifyAsyncOperation();
const router = usePastaRouter();
const authStore = useAuthStore();
const alertManager = useAlertManager();

const { refresh, letterFile } = inject<FormProvide>(FormToken)!;

const isDeleteLFModalOpen = ref(false);
const isNextStepLoading = ref(false);

const isCurrent = computed(() => letterFile.value?.status === LetterFileStatus.CURRENT);
const isDraft = computed(() => letterFile.value?.draft);
const isDeletable = computed(() => letterFile.value?.isDeletable);
const nextElementLabel = computed(() =>
  props.next ? `Le parapheur va passer à l'étape "${props.next}"` : 'Le parapheur va passer à l\'étape "Archivé"'
);

async function take() {
  await notifyAsyncOperation(async () => {
    await letterFileStore.take(letterFile.value!.id);
    await refresh();
  }, FormOperation.TAKE);
  alertManager.createAlert({
    message: "Parapheur à votre niveau",
    type: AlertType.SUCCESS,
    duration: AlertDuration.MEDIUM,
  });
}

async function unarchive() {
  if (
    confirm(
      "Les documents présents dans le parapheur ne pourront ni être modifiés ni signés. Êtes vous sûr(e) de vouloir désarchiver le parapheur ?"
    )
  ) {
    await notifyAsyncOperation(async () => {
      await letterFileStore.unarchive(letterFile.value!.id);
      await refresh();
    }, FormOperation.UNARCHIVE);
    alertManager.createAlert({
      message: "Parapheur désarchivé",
      type: AlertType.SUCCESS,
      duration: AlertDuration.MEDIUM,
    });
  }
}

async function deleteLetterFile() {
  await letterFileStore.delete(letterFile.value!.id);
  letterFileStore.setCurrentId(null);
  router.push("/");
}

const navigationChecker = useNavigationChecker();
const notifySigned = useShowSignedNotified();

async function nextStep() {
  isNextStepLoading.value = true;
  navigationChecker.check();
  if (!canGoNext(letterFile.value!)) {
    isNextStepLoading.value = false;
    return;
  }

  const hasSigned = letterFile?.value?.current?.hasSigned;

  if (isDraft.value) {
    emit("undraft");
    isNextStepLoading.value = false;
  } else {
    const next = props.next;
    await notifyAsyncOperation(async () => {
      await letterFileStore.next(letterFile.value!.id);
    }, FormOperation.NEXT);

    const message = next ? `Envoyé à ${next}` : "Archivé";
    alertManager.createAlert({
      message,
      type: AlertType.SUCCESS,
      duration: AlertDuration.MEDIUM,
    });

    if (hasSigned) {
      notifySigned.register(letterFile.value!);
    }

    router.push("/");
    isNextStepLoading.value = false;
  }
}

// add step
const newElement = ref<boolean>(false);
const agentsDirectoryEntry = ref<AgentsDirectoryAutocompleteItem[]>([]);
const selectUserEl = ref<typeof SelectUser | null>(null);

async function openNewElement() {
  newElement.value = true;
  await nextTick();
  selectUserEl.value?.focus();
}

function closeNewElement() {
  agentsDirectoryEntry.value = [];
  newElement.value = false;
}

async function addElement() {
  await notifyAsyncOperation(async () => {
    for (const val of agentsDirectoryEntry.value) {
      await letterFileStore.appendDownstream(letterFile.value!.id, val.mail);
    }
    await refresh();
    emit("newDownstream");
  }, FormOperation.FLUX);
  closeNewElement();
}
</script>

<style>
.shadow-top {
  box-shadow:
    0 -4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
}
</style>
../../../dto/letter-file.dto../../../stores/letter-file.store
